import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Building2, ShieldCheck, ShieldOff, UserCog, Copy,
  Loader2,
} from "lucide-react";
import {
  adminGetOrganization, adminTransitionOrgStatus,
  adminUpdateUserLimit, adminDesignateInitialAdmin,
  formatCommunityPartnerError,
} from "@/services/communityPartnerApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const ADMIN_STATUSES = ["Draft", "Approved", "Active", "Suspended", "Inactive", "Closed"];

/**
 * HC-EPIC-004 · Sprint 1-B · HarborWell admin — CP Organization detail.
 *
 * Route: `/admin/community-partner-organizations/:id`
 * Owner directive §6B.
 */
export default function AdminCommunityPartnerOrganizationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [nextStatus, setNextStatus] = useState("");
  const [statusReason, setStatusReason] = useState("");
  const [statusBusy, setStatusBusy] = useState(false);

  const [nextLimit, setNextLimit] = useState("");
  const [limitReason, setLimitReason] = useState("");
  const [limitBusy, setLimitBusy] = useState(false);

  const [initialAdminEmail, setInitialAdminEmail] = useState("");
  const [initialAdminName, setInitialAdminName] = useState("");
  const [initialAdminBusy, setInitialAdminBusy] = useState(false);
  const [pendingInitialAdmin, setPendingInitialAdmin] = useState(null);

  const refresh = useCallback(async () => {
    setErr("");
    try {
      const data = await adminGetOrganization(id);
      setDetail(data);
      setNextLimit(String(data?.organization?.user_limit ?? 25));
    } catch (e) {
      setErr(formatCommunityPartnerError(e, "Unable to load organization."));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  const submitStatus = async () => {
    if (!nextStatus || statusBusy) return;
    setStatusBusy(true);
    try {
      await adminTransitionOrgStatus(id, nextStatus, statusReason);
      toast.success(`Status set to ${nextStatus}.`);
      setNextStatus(""); setStatusReason("");
      refresh();
    } catch (e) {
      toast.error(formatCommunityPartnerError(e, "Unable to change status."));
    } finally {
      setStatusBusy(false);
    }
  };

  const submitLimit = async () => {
    const n = Number(nextLimit);
    if (!n || limitBusy) return;
    setLimitBusy(true);
    try {
      await adminUpdateUserLimit(id, n, limitReason);
      toast.success("User limit updated.");
      setLimitReason("");
      refresh();
    } catch (e) {
      toast.error(formatCommunityPartnerError(e, "Unable to update user limit."));
    } finally {
      setLimitBusy(false);
    }
  };

  const submitInitialAdmin = async (e) => {
    e.preventDefault();
    if (!initialAdminEmail.trim() || initialAdminBusy) return;
    setInitialAdminBusy(true);
    try {
      const data = await adminDesignateInitialAdmin(id, {
        email: initialAdminEmail.trim(),
        displayName: initialAdminName.trim(),
      });
      setPendingInitialAdmin(data);
      toast.success("Initial administrator invitation created.");
      setInitialAdminEmail("");
      setInitialAdminName("");
      refresh();
    } catch (e) {
      toast.error(formatCommunityPartnerError(e, "Unable to designate initial administrator."));
    } finally {
      setInitialAdminBusy(false);
    }
  };

  const copyLink = (token) => {
    const link = `${window.location.origin}/community-partner-portal/activate?token=${encodeURIComponent(token)}`;
    navigator.clipboard?.writeText(link).then(
      () => toast.success("Activation link copied to clipboard."),
      () => toast.error("Copy failed. Please select and copy manually.")
    );
  };

  if (loading) return <div className="p-8 text-slate-500 flex items-center gap-2"
                          data-testid="admin-cp-org-detail-loading">
    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
  </div>;
  if (err) return <div className="p-8"><Alert variant="destructive"
                            data-testid="admin-cp-org-detail-error"><AlertDescription>{err}</AlertDescription></Alert></div>;
  const org = detail?.organization || {};
  const counts = detail?.counts || {};

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="admin-cp-org-detail-page">
      <div className="flex items-center justify-between">
        <Button variant="ghost"
                onClick={() => nav("/admin/community-partner-organizations")}
                data-testid="admin-cp-org-detail-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to organizations
        </Button>
      </div>

      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-slate-900 text-white p-2">
            <Building2 className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-slate-900"
                data-testid="admin-cp-org-detail-title">
              {org.display_name || org.name}
            </h1>
            <p className="text-sm text-slate-500">{org.name}</p>
          </div>
        </div>
        <Badge variant="outline" data-testid={`admin-cp-org-status-${org.status}`}>{org.status}</Badge>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Metric title="Active users" value={counts.active_users ?? "—"}
                testId="admin-cp-org-count-active-users" />
        <Metric title="Invited users" value={counts.invited_users ?? "—"}
                testId="admin-cp-org-count-invited-users" />
        <Metric title="Pending invitations" value={counts.pending_invitations ?? "—"}
                testId="admin-cp-org-count-pending-invitations" />
      </div>

      {/* Lifecycle transitions */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6"
           data-testid="admin-cp-org-lifecycle-card">
        <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" /> Lifecycle
        </h2>
        <div className="grid gap-3 md:grid-cols-3">
          <div>
            <Label htmlFor="admin-cp-org-next-status">Next status</Label>
            <Select value={nextStatus} onValueChange={setNextStatus}>
              <SelectTrigger id="admin-cp-org-next-status" data-testid="admin-cp-org-status-trigger">
                <SelectValue placeholder="Select…" />
              </SelectTrigger>
              <SelectContent>
                {ADMIN_STATUSES.filter((s) => s !== org.status).map((s) => (
                  <SelectItem key={s} value={s} data-testid={`admin-cp-org-status-option-${s}`}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="admin-cp-org-status-reason">Reason (recorded in audit log)</Label>
            <Input id="admin-cp-org-status-reason"
                   data-testid="admin-cp-org-status-reason-input"
                   value={statusReason} onChange={(e) => setStatusReason(e.target.value)} />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={submitStatus} disabled={!nextStatus || statusBusy}
                  data-testid="admin-cp-org-status-submit">
            {statusBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Change status"}
          </Button>
        </div>
      </div>

      {/* User-limit */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6"
           data-testid="admin-cp-org-limit-card">
        <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <UserCog className="w-4 h-4" /> Active-user limit
        </h2>
        <div className="grid gap-3 md:grid-cols-3">
          <div>
            <Label htmlFor="admin-cp-org-limit">User limit</Label>
            <Input id="admin-cp-org-limit" type="number" min={1} max={500}
                   data-testid="admin-cp-org-limit-input"
                   value={nextLimit} onChange={(e) => setNextLimit(e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="admin-cp-org-limit-reason">Reason</Label>
            <Input id="admin-cp-org-limit-reason"
                   data-testid="admin-cp-org-limit-reason-input"
                   value={limitReason} onChange={(e) => setLimitReason(e.target.value)} />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={submitLimit} disabled={limitBusy}
                  data-testid="admin-cp-org-limit-submit">
            {limitBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update limit"}
          </Button>
        </div>
      </div>

      {/* Initial administrator */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6"
           data-testid="admin-cp-org-initial-admin-card">
        <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <ShieldOff className="w-4 h-4" /> Designate initial organization administrator
        </h2>
        <p className="text-sm text-slate-500 mb-3">
          Available while the organization is Approved or Active. The invitee
          receives a one-time activation link and must complete MFA enrolment
          before gaining portal access.
        </p>
        <form onSubmit={submitInitialAdmin} className="grid gap-3 md:grid-cols-3">
          <div>
            <Label htmlFor="admin-cp-org-ia-email">Email</Label>
            <Input id="admin-cp-org-ia-email" type="email" required
                   data-testid="admin-cp-org-initial-admin-email-input"
                   value={initialAdminEmail}
                   onChange={(e) => setInitialAdminEmail(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="admin-cp-org-ia-name">Display name (optional)</Label>
            <Input id="admin-cp-org-ia-name"
                   data-testid="admin-cp-org-initial-admin-name-input"
                   value={initialAdminName}
                   onChange={(e) => setInitialAdminName(e.target.value)} />
          </div>
          <div className="flex items-end">
            <Button type="submit" disabled={initialAdminBusy}
                    data-testid="admin-cp-org-initial-admin-submit"
                    className="w-full md:w-auto">
              {initialAdminBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send invitation"}
            </Button>
          </div>
        </form>
        {pendingInitialAdmin?.activation_token && (
          <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200 text-sm"
               data-testid="admin-cp-org-initial-admin-result">
            <div className="font-medium text-amber-900 mb-1">
              Copy the activation link — it will not be shown again.
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-white p-2 rounded border border-amber-200 break-all">
                {`${window.location.origin}/community-partner-portal/activate?token=${pendingInitialAdmin.activation_token}`}
              </code>
              <Button size="sm" variant="secondary"
                      onClick={() => copyLink(pendingInitialAdmin.activation_token)}
                      data-testid="admin-cp-org-initial-admin-copy">
                <Copy className="w-3.5 h-3.5 mr-1" /> Copy
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Metric({ title, value, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4"
         data-testid={testId}>
      <div className="text-xs uppercase tracking-wider text-slate-500">{title}</div>
      <div className="text-2xl font-semibold text-slate-900">{value}</div>
    </div>
  );
}
